// src/common/MetricAIAssistant.jsx

import React, { useState } from 'react';
import { AutoAwesome, Close } from '@mui/icons-material';
import { fetchMetricInsightFromAPI } from '../services/aiService'

const MetricAIAssistant = ({ metricName, metricData, token }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [insight, setInsight] = useState(null);
  const [error, setError] = useState(null);

  const handleFetchInsight = async () => {
    setIsOpen(true);
    if (insight) return; // Client-side cache: don't re-fetch if already loaded for this view

    setLoading(true);
    setError(null);

    try {
      const data = await fetchMetricInsightFromAPI(metricName, metricData, token);
      setInsight(data);
    } catch (err) {
      console.error("AI Insight Fetch Error:", err);
      setError("Unable to generate AI analysis at this time.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Clickable Sparkle Trigger */}
      <button 
        onClick={handleFetchInsight}
        className="p-1.5 rounded-full text-indigo-600 hover:bg-indigo-50 hover:scale-105 transition-all duration-150"
        title={`Get AI Insights for ${metricName}`}
      >
        <AutoAwesome className="w-5 h-5" />
      </button>

      {/* Slide-over Drawer / Modal */}
      {isOpen && (
        <div 
          onClick={() => setIsOpen(false)} 
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex justify-end animate-in fade-in duration-200"
        >
          {/* stopPropagation prevents clicks inside the sidebar from closing it */}
          <div 
            onClick={(e) => e.stopPropagation()} 
            className="bg-white w-full max-w-md h-full shadow-2xl p-6 flex flex-col justify-between overflow-y-auto animate-in slide-in-from-right duration-300 relative"
          >
            
            {/* Header */}
            <div>
              <div className="flex items-center justify-between border-b pb-4 mb-4">
                <div className="flex items-center gap-2">
                  <AutoAwesome className="text-indigo-600" />
                  <h3 className="font-bold text-gray-900 text-lg">{metricName} Insight</h3>
                </div>
                <button 
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-gray-600 p-1.5 rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
                  title="Close panel"
                >
                  <Close />
                </button>
              </div>

              {/* State 1: Loading */}
              {loading && (
                <div className="py-12 text-center space-y-3">
                  <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
                  <p className="text-sm font-medium text-gray-500">
                    Running <span className="font-mono text-indigo-600">Wow Carz AI</span> analysis...
                  </p>
                </div>
              )}

              {/* State 2: Error */}
              {error && (
                <div className="p-4 bg-red-50 text-red-700 text-sm rounded-lg border border-red-200">
                  {error}
                </div>
              )}

              {/* State 3: Insight Render */}
              {!loading && insight && (
                <div className="space-y-5">
                  {/* Status Badge */}
                  <div className="flex items-center justify-between bg-slate-50 p-3 rounded-lg border border-slate-200">
                    <span className="text-xs font-semibold uppercase text-gray-500">Status Tag</span>
                    <span className={`px-2.5 py-1 text-xs font-bold rounded-full ${
                      insight.statusTag === 'CRITICAL' ? 'bg-red-100 text-red-700' :
                      insight.statusTag === 'WARNING' ? 'bg-amber-100 text-amber-700' :
                      'bg-emerald-100 text-emerald-700'
                    }`}>
                      {insight.statusTag}
                    </span>
                  </div>

                  {/* Status Summary */}
                  <div>
                    <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Current Status</h4>
                    <p className="text-sm text-gray-800 leading-relaxed font-medium">
                      {insight.statusSummary}
                    </p>
                  </div>

                  {/* Benchmark / Comparison */}
                  <div className="border-l-4 border-indigo-500 pl-3 py-1 bg-indigo-50/60 rounded-r-md">
                    <h4 className="text-xs font-semibold text-indigo-900 uppercase tracking-wider">Comparison Benchmark</h4>
                    <p className="text-sm text-indigo-950 mt-1">{insight.comparisonText}</p>
                  </div>

                  {/* Recommendations */}
                  <div>
                    <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Recommended Actions</h4>
                    <div className="space-y-2">
                      {insight.actionableSuggestions?.map((rec, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm text-gray-700 bg-gray-50 p-3 rounded-lg border border-gray-200">
                          <span className="text-indigo-600 font-bold">•</span>
                          <span>{rec}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t pt-4 text-xs text-center text-gray-400">
              Powered by LLM Agent API (`llama3.3-70b-instruct`)
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MetricAIAssistant;